import React, { useState, useEffect } from "react";
import { DragDropContext } from "@hello-pangea/dnd";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Grid,
  Paper,
  FormControlLabel,
  Checkbox,
  Divider
} from "@mui/material";
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import "react-datepicker/dist/react-datepicker.css";
import Column from "../../componentes/Column";
import { useAuth } from "../../context/AuthContext";
import { getActivosByFechaInicio } from "../../services/vulnerabilidades/vulnerabilidadActivo/vulnerabilidadActivo";
import { DayPicker } from "react-day-picker";
import { default as defaultStyles } from "react-day-picker/dist/style.module.css";
import { es } from "react-day-picker/locale";
import { toast } from "react-toastify";

const SeleccionarActivo = ({
  onRowsUpdate,
  setSelectedIds,
  setSelectedDates,
  lista2,
  setLista2,
}) => {
  const [activosFiltrados, setActivosFiltrados] = useState([]);
  const [columns, setColumns] = useState({
    Activos: { id: "Activos", list: [] },
    Escoger: { id: "Escoger", list: [] },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const emp_codigo = user?.emp_codigo;
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [showAll, setShowAll] = useState(false);

  const groupActivosByCodigo = (data) => {
    const grouped = data.reduce((acc, item) => {
      const key = item.act_codigo;
      if (!acc[key]) {
        acc[key] = {
          id: `${item.act_codigo}`,
          nombre: item.act_nombre,
          instancias: [],
        };
      }
      acc[key].instancias.push({
        act_codigo: item.act_codigo,
        vul_codigo: item.vul_codigo,
        vac_fecha_inicio: item.vac_fecha_inicio,
        vac_costo: item.vac_costo,
        ame_nombre: item.ame_nombre || "Sin amenaza definida",
        vul_nombre: item.vul_nombre || "Sin vulnerabilidad definida",
      });
      return acc;
    }, {});
    return Object.values(grouped);
  };

  const fetchActivos = async (start, end) => {
    setLoading(true);
    setError(null);
    try {
      const activosPorFecha = await getActivosByFechaInicio(start, end, emp_codigo);
      const activosFiltradosSinDuplicados = activosPorFecha.filter(
        (activo) => !lista2.some((sel) => sel.act_codigo === activo.act_codigo)
      );

      setColumns((prevColumns) => ({
        ...prevColumns,
        Activos: {
          ...prevColumns.Activos,
          list: groupActivosByCodigo(activosFiltradosSinDuplicados),
        },
      }));
    } catch (error) {
      setError("Error al cargar los activos.");
    } finally {
      setLoading(false);
    }
  };

  const handleRangeChange = (selectedRange) => {
    setRange(selectedRange);
    if (selectedRange?.from && selectedRange?.to) {
      const pad = (n) => n.toString().padStart(2, "0");
      const fechaInicio = `${selectedRange.from.getFullYear()}-${pad(selectedRange.from.getMonth() + 1)}-${pad(selectedRange.from.getDate())}`;
      const fechaFin = `${selectedRange.to.getFullYear()}-${pad(selectedRange.to.getMonth() + 1)}-${pad(selectedRange.to.getDate())}`;
      fetchActivos(fechaInicio, fechaFin);
    }
  };

  const handleShowAllChange = (event) => {
    const checked = event.target.checked;
    setShowAll(checked);
    if (checked) {
      // Fetch with a very wide range to simulate "All"
      fetchActivos("2000-01-01", "2100-12-31");
      setRange({ from: undefined, to: undefined });
    } else {
      // Clear list if unchecked and no range selected
      setColumns((prev) => ({ ...prev, Activos: { ...prev.Activos, list: [] } }));
    }
  };

  useEffect(() => {
    // Initial load: maybe load today's assets or nothing?
    // User requested "Show All" might be better as default? 
    // For now, let's stick to the previous behavior (today) but respect the checkbox if we wanted to persist it.
    // But actually, let's just load today's date initially as before.
    const hoy = new Date();
    const fecha = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-${String(hoy.getDate()).padStart(2, "0")}`;
    fetchActivos(fecha, fecha);
  }, [emp_codigo]);

  const guardarSeleccion = (nuevaLista2) => {
    if (!nuevaLista2 || nuevaLista2.length === 0) {
      setSelectedIds([]);
      setSelectedDates([]);
      return;
    }
    const tempSelectedIds = nuevaLista2.map((item) => item.act_codigo);
    const tempSelectedDates = nuevaLista2.map((item) => item.vac_fecha_inicio);

    setSelectedIds(tempSelectedIds);
    setSelectedDates(tempSelectedDates);
    toast.success("Selección actualizada");
  };

  const onDragEnd = ({ source, destination }) => {
    if (!destination) return;

    if (
      source.droppableId === destination.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const start = columns[source.droppableId];
    const end = columns[destination.droppableId];
    const transferredItem = start.list[source.index];

    let nuevaLista2 = lista2;

    if (end.id === "Escoger") {
      nuevaLista2 = [...lista2, ...transferredItem.instancias];
      setLista2(nuevaLista2);
    } else if (end.id === "Activos") {
      const codigosAEliminar = transferredItem.instancias.map(i => i.act_codigo + "_" + i.vac_fecha_inicio);
      nuevaLista2 = lista2.filter(
        (item) => !codigosAEliminar.includes(item.act_codigo + "_" + item.vac_fecha_inicio)
      );
      setLista2(nuevaLista2);
    }

    if (start === end) {
      const newList = start.list.filter((_, idx) => idx !== source.index);
      newList.splice(destination.index, 0, transferredItem);
      setColumns((state) => ({
        ...state,
        [start.id]: { ...start, list: newList },
      }));
    } else {
      const newStartList = start.list.filter((_, idx) => idx !== source.index);
      const newEndList = [...end.list];
      newEndList.splice(destination.index, 0, transferredItem);
      setColumns((state) => ({
        ...state,
        [start.id]: { ...start, list: newStartList },
        [end.id]: { ...end, list: newEndList },
      }));
    }
    guardarSeleccion(nuevaLista2);
  };

  return (
    <Box sx={{ p: 1 }}>
      <Grid container spacing={2}>
        {/* Left Column: Filters */}
        <Grid item xs={12} md={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #E2E8F0', height: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                mb: 1.5,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#0F172A',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Filtros
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAll}
                    onChange={handleShowAllChange}
                    sx={{
                      color: '#64748b',
                      '&.Mui-checked': {
                        color: '#001F3F',
                      },
                    }}
                    size="small"
                  />
                }
                label={
                  <Typography variant="caption" fontWeight={500} color="text.secondary">
                    Ver todos los activos
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="caption" sx={{ mb: 1, display: 'block', color: "text.secondary", fontWeight: 600 }}>
              Rango de fechas
            </Typography>

            <Box sx={{
              opacity: showAll ? 0.5 : 1,
              pointerEvents: showAll ? 'none' : 'auto',
              transform: 'scale(0.9)', // Scale down slightly to fit better
              transformOrigin: 'top left',
              mb: -2 // Compensate for scale
            }}>
              <DayPicker
                locale={es}
                fixedWeeks
                showOutsideDays
                navLayout="around"
                mode="range"
                selected={range}
                onSelect={handleRangeChange}
                classNames={defaultStyles}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Right Column: Drag and Drop Area */}
        <Grid item xs={12} md={9}>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            {/* Legend - Integrated into top bar or separate small bar? Let's keep it separate but compact */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{
                p: 1,
                borderRadius: 2,
                border: '1px solid #E2E8F0',
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                bgcolor: '#F8FAFC'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningAmberRoundedIcon sx={{ color: '#3B82F6', fontSize: '1.2rem' }} />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Amenazas
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SecurityRoundedIcon sx={{ color: '#EC4899', fontSize: '1.2rem' }} />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Vulnerabilidades
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Column col={columns.Activos} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Column col={columns.Escoger} />
                  </Grid>
                </Grid>
              </DragDropContext>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default SeleccionarActivo;
